/**
 * 「现在是不是手机布局」。
 *
 * 判据是 **触屏 + 窄**，不是 UA：
 *  · iPad 横屏 1180 宽，但它是触屏，工具条按钮得留够手指的尺寸；
 *  · 桌面浏览器缩到 500 宽仍是鼠标操作，不该切成手机布局。
 * 单靠宽度或单靠 UA 都会判错一头。
 *
 * 阈值 640 与 styles.css 里的 `@media (max-width: 640px)` 一致，
 * 两边必须同一个数，否则 JS 认为是手机、CSS 还按桌面排。
 */

import { useEffect, useState } from 'react'

export const MOBILE_MAX_WIDTH = 640

export function isMobileLayout(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false
  // 按**短边**判断：横屏手机宽 852 但高只有 393，按宽判会把它当成桌面，
  // 于是横屏——本该最适合看课件的姿势——反而套上了桌面布局。
  const shortSide = Math.min(window.innerWidth, window.innerHeight)
  return window.matchMedia('(pointer: coarse)').matches && shortSide <= MOBILE_MAX_WIDTH
}

/** 组件里用，尺寸变化时跟着更新。 */
export function useMobileLayout(): boolean {
  const [mobile, setMobile] = useState(isMobileLayout)
  useEffect(() => {
    const update = () => setMobile(isMobileLayout())
    window.addEventListener('resize', update)
    window.addEventListener('orientationchange', update)
    return () => {
      window.removeEventListener('resize', update)
      window.removeEventListener('orientationchange', update)
    }
  }, [])
  return mobile
}

/** 横屏手机：触屏且矮。与 styles.css 的 `(pointer: coarse) and (max-height: 500px)` 一致。 */
export function isMobileLandscape(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false
  return window.matchMedia('(pointer: coarse)').matches && window.innerHeight <= 500
}

/**
 * 组件里用的横屏判定，尺寸变化时跟着更新。
 * 不能只靠 useMobileLayout 触发重渲染：竖屏转横屏前后都算「手机」，那个值不变，
 * 组件就不会重渲染，横屏沉浸模式永远起不来。
 */
export function useMobileLandscape(): boolean {
  const [landscape, setLandscape] = useState(isMobileLandscape)
  useEffect(() => {
    const update = () => setLandscape(isMobileLandscape())
    window.addEventListener('resize', update)
    window.addEventListener('orientationchange', update)
    return () => {
      window.removeEventListener('resize', update)
      window.removeEventListener('orientationchange', update)
    }
  }, [])
  return landscape
}
